import * as React from "react";
import { VideoPlayer } from "@/v2/components/VideoPlayer";
import { Button } from "@/v2/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/v2/components/ui/dialog";
import { formatTimecode } from "../schedulerUtils";
import type { Program } from "../types";
import { MediaThumb } from "./parts";

interface Props {
  program: Program;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onApply: (trimInSec: number, trimOutSec: number) => void;
}

/** Preview with two sliders for the trim in/out points. Plays the clip itself
 * when its manifest is known (any program that has round-tripped through a
 * save); a program still waiting on its first save (no videoUrl yet) falls
 * back to the team-logo tile it shows on the timeline. */
export function TrimDialog({ program, open, onOpenChange, onApply }: Props) {
  const [range, setRange] = React.useState<[number, number]>([program.trimInSec, program.trimOutSec]);
  const playerRef = React.useRef<any>(null);

  React.useEffect(() => {
    if (open) setRange([program.trimInSec, program.trimOutSec]);
  }, [open, program.trimInSec, program.trimOutSec]);

  const [inSec, outSec] = range;
  const max = program.durationSec;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent style={{ padding: 24 }}>
        <DialogHeader>
          <DialogTitle>Set trim points</DialogTitle>
          <DialogDescription>{program.title}</DialogDescription>
        </DialogHeader>
        {program.videoUrl ? (
          <div
            className="relative w-full overflow-hidden rounded-md bg-black"
            style={{ aspectRatio: "16 / 9" }}
          >
            <VideoPlayer
              className="h-full w-full"
              hlsUrl={program.videoUrl}
              title={program.title}
              startOffsetSeconds={inSec}
              playerRef={playerRef}
            />
          </div>
        ) : (
          <MediaThumb
            thumbnail={program.thumbnail}
            logos={program.logos}
            className="w-full rounded-md"
            style={{ aspectRatio: "16 / 9" }}
          />
        )}
        <div className="flex flex-col" style={{ gap: 12 }}>
          <label className="flex items-center justify-between text-sm" style={{ gap: 12, color: "var(--v2-foreground)", margin: 0 }}>
            <span style={{ width: 70 }}>Trim in</span>
            <input
              type="range"
              aria-label="Trim in point"
              min={0}
              max={max}
              value={inSec}
              onChange={(e) => {
                const next = Math.min(Number(e.target.value), outSec - 1);
                setRange([next, outSec]);
                playerRef.current?.seek?.(next);
              }}
              className="flex-1"
            />
            <span className="tabular-nums" style={{ width: 70, textAlign: "right" }}>
              {formatTimecode(inSec)}
            </span>
          </label>
          <label className="flex items-center justify-between text-sm" style={{ gap: 12, color: "var(--v2-foreground)", margin: 0 }}>
            <span style={{ width: 70 }}>Trim out</span>
            <input
              type="range"
              aria-label="Trim out point"
              min={0}
              max={max}
              value={outSec}
              onChange={(e) => {
                const next = Math.max(Number(e.target.value), inSec + 1);
                setRange([inSec, next]);
                playerRef.current?.seek?.(next);
              }}
              className="flex-1"
            />
            <span className="tabular-nums" style={{ width: 70, textAlign: "right" }}>
              {formatTimecode(outSec)}
            </span>
          </label>
        </div>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            className="bg-action text-white hover:bg-action-hover"
            onClick={() => {
              onApply(inSec, outSec);
              onOpenChange(false);
            }}
          >
            Apply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
